/**
 * Branded character smoke — Casa Aura (Variant E)
 *
 * Extends Variant C: continuous glyph particles (✦ ∿ ~ ○ ° ✧ ❋)
 * PLUS, every ~10 seconds, "CASA AURA" rises together as a complete word.
 * Letters spawn side-by-side so they're readable, then drift apart naturally
 * as they rise — smoke that spells the brand.
 */
(function (root) {
  'use strict';
  if (root.SmokeBrandChars) return;

  var GLYPHS     = ['✦', '∿', '~', '·', '○', '°', '✧', '❋', '◦', '∾', '⁓'];
  var WORD       = 'casa aura';
  var WORD_EVERY = 600; // frames between word bursts (~10 s at 60 fps)

  // ── Particle ────────────────────────────────────────────────────────────────

  function Particle(x, oy, isLetter, char, baseSize) {
    this.isLetter = !!isLetter;
    this.x  = x;
    this.y  = oy;
    this.oy = oy;

    // Letters rise together — very small initial horizontal drift
    var driftScale = isLetter ? 0.08 : 0.35;
    this.vx = (Math.random() - 0.5) * driftScale;
    this.vy = -(Math.random() * 0.55 + 0.45);

    this.char     = char || GLYPHS[Math.floor(Math.random() * GLYPHS.length)];
    this.baseSize = baseSize || (Math.random() * 10 + 10);
    this.size     = this.baseSize;

    this.life    = 0;
    this.maxLife = Math.floor(Math.random() * 220 + 300);

    // Letters: minimal rotation so they stay readable while rising
    this.rotation = isLetter ? (Math.random() - 0.5) * 0.1 : Math.random() * Math.PI * 2;
    this.rotSpeed = isLetter ? (Math.random() - 0.5) * 0.004 : (Math.random() - 0.5) * 0.016;
    this.turbOffset = Math.random() * 1000;

    this.maxAlpha = isLetter ? 0.70 + Math.random() * 0.20 : 0.45 + Math.random() * 0.30;
    this.alpha    = 0;
  }

  Particle.prototype.update = function (t, ox, oy, canvasH) {
    this.life++;
    if (this.life >= this.maxLife) return;

    var progress = this.life / this.maxLife;
    var rise     = Math.max(0, (oy - this.y) / (canvasH || oy));

    // Turbulence — letters start tight, gently drift apart as they rise
    var turbScale = this.isLetter ? (0.08 + rise * 0.5) : (0.25 + rise * 0.8);
    this.vx += Math.sin(t * 0.022 + this.turbOffset) * 0.028 * turbScale;
    this.vx *= 0.965;
    this.x  += this.vx;
    this.y  += this.vy;
    this.vy *= 0.998;

    this.rotation += this.rotSpeed;
    this.size = this.baseSize * Math.max(0.25, 1 - rise * 0.80);

    if (progress < 0.08) {
      this.alpha = this.maxAlpha * (progress / 0.08);
    } else if (progress < 0.72) {
      this.alpha = this.maxAlpha;
    } else {
      this.alpha = this.maxAlpha * (1 - (progress - 0.72) / 0.28);
    }
  };

  Particle.prototype.draw = function (ctx) {
    if (this.char === ' ') return; // space = gap, no draw
    ctx.save();
    ctx.translate(this.x, this.y);
    ctx.rotate(this.rotation);
    ctx.globalAlpha = this.alpha;

    if (this.isLetter) {
      ctx.font      = 'normal ' + this.size + 'px Georgia, "Times New Roman", serif';
      ctx.fillStyle = 'rgb(60, 62, 70)';
    } else {
      ctx.font      = this.size + 'px serif';
      ctx.fillStyle = 'rgb(85, 88, 95)';
    }

    ctx.textAlign    = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(this.char, 0, 0);
    ctx.restore();
  };

  Object.defineProperty(Particle.prototype, 'dead', {
    get: function () { return this.life >= this.maxLife || this.y < -120; }
  });

  // ── SmokeBrandChars ─────────────────────────────────────────────────────────

  function SmokeBrandChars(canvas, opts) {
    this.canvas = canvas;
    this.ctx    = canvas.getContext('2d');
    this.opts   = Object.assign({ autoplay: true, showEmber: true }, opts || {});

    this.particles  = [];
    this.time       = 0;
    this._frame     = 0;
    this._nextWord  = 120; // spawn first word burst after 2 seconds
    this.running    = false;
    this._raf       = null;

    this._resize = this._resize.bind(this);
    this._tick   = this._tick.bind(this);
    window.addEventListener('resize', this._resize);
    this._doResize();

    // Pre-seed glyphs
    for (var i = 0; i < 18; i++) {
      var p = new Particle(this.ox + (Math.random() - 0.5) * 8, this.oy, false);
      p.life = Math.floor(Math.random() * p.maxLife * 0.6);
      this.particles.push(p);
    }

    if (this.opts.autoplay) this._setupIO();
  }

  SmokeBrandChars.prototype._doResize = function () {
    var parent = this.canvas.parentElement;
    this.canvas.width  = parent.offsetWidth;
    this.canvas.height = parseInt(this.canvas.dataset.height, 10) || 400;
    this.ox = this.canvas.width / 2;
    var ob = parseInt(this.canvas.dataset.originBottom, 10);
    this.oy = ob > 0 ? this.canvas.height - ob : this.canvas.height * 0.92;
    // Old particles were positioned relative to the previous canvas size —
    // clearing avoids them appearing transposed/stretched after resize
    if (this.particles) this.particles.length = 0;
  };

  SmokeBrandChars.prototype._resize = function () {
    var self = this;
    clearTimeout(this._resizeDebounce);
    this._resizeDebounce = setTimeout(function () { self._doResize(); }, 120);
  };

  SmokeBrandChars.prototype._setupIO = function () {
    var self = this;
    new IntersectionObserver(function (e) {
      e.forEach(function (en) { en.isIntersecting ? self.start() : self.stop(); });
    }, { threshold: 0.05 }).observe(this.canvas);
  };

  SmokeBrandChars.prototype.applyWind = function (mouseX, windVX) {
    var strength = Math.min(0.8, Math.abs(windVX) * 0.12);
    var dir = windVX >= 0 ? 1 : -1;
    for (var i = 0; i < this.particles.length; i++) {
      var p = this.particles[i];
      var dx = Math.abs(p.x - mouseX);
      if (dx < 100) {
        p.vx += dir * strength * (1 - dx / 100) * 0.4;
      }
    }
  };

  SmokeBrandChars.prototype.start = function () {
    if (this.running) return;
    this.running = true;
    this._raf = requestAnimationFrame(this._tick);
  };

  SmokeBrandChars.prototype.stop = function () {
    this.running = false;
    if (this._raf) { cancelAnimationFrame(this._raf); this._raf = null; }
  };

  // Spawn "CASA AURA" as a vertical column — C at top, A at bottom
  // Letters are pre-stacked so they rise together reading top→bottom
  SmokeBrandChars.prototype._spawnWord = function () {
    var letterSize = Math.min(18, this.canvas.width / 22);
    var letterGap  = letterSize * 1.5; // vertical gap between letters
    var wordLen    = WORD.length;

    for (var i = 0; i < wordLen; i++) {
      // i=0 (C) is at top: oy - (wordLen-1)*gap
      // i=last (A) is at bottom: oy
      var y  = this.oy - (wordLen - 1 - i) * letterGap;
      var p  = new Particle(
        this.ox + (Math.random() - 0.5) * 4, // tight column, tiny x jitter
        y,
        true,
        WORD[i],
        letterSize
      );
      // All letters same rise speed — column stays intact while rising
      p.vy = -(Math.random() * 0.04 + 0.50);
      this.particles.push(p);
    }
  };

  SmokeBrandChars.prototype._tick = function () {
    if (!this.running) return;
    this.time++;
    this._frame++;

    // Spawn glyphs every 3 frames
    if (this._frame % 3 === 0 && this.particles.length < 48) {
      this.particles.push(
        new Particle(this.ox + (Math.random() - 0.5) * 8, this.oy, false)
      );
    }

    // Spawn word burst on schedule
    if (this._frame >= this._nextWord) {
      this._spawnWord();
      this._nextWord = this._frame + WORD_EVERY;
    }

    // Cull dead particles
    for (var i = this.particles.length - 1; i >= 0; i--) {
      if (this.particles[i].dead) this.particles.splice(i, 1);
    }

    var ctx = this.ctx;
    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

    // Ember glow
    if (this.opts.showEmber) {
      if (this.opts.bigEmber) {
        // Vibe zone: prominent 3-layer ember
        var g2 = ctx.createRadialGradient(this.ox, this.oy, 0, this.ox, this.oy, 28);
        g2.addColorStop(0,   'rgba(255,180,60,0.30)');
        g2.addColorStop(0.4, 'rgba(200,80,10,0.18)');
        g2.addColorStop(1,   'rgba(130,35,0,0)');
        ctx.fillStyle = g2;
        ctx.beginPath(); ctx.arc(this.ox, this.oy, 28, 0, Math.PI * 2); ctx.fill();
        var g3 = ctx.createRadialGradient(this.ox, this.oy, 0, this.ox, this.oy, 6);
        g3.addColorStop(0, 'rgba(255,240,180,0.95)');
        g3.addColorStop(1, 'rgba(255,120,20,0)');
        ctx.fillStyle = g3;
        ctx.beginPath(); ctx.arc(this.ox, this.oy, 6, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,245,200,0.9)';
        ctx.beginPath(); ctx.arc(this.ox, this.oy, 2, 0, Math.PI * 2); ctx.fill();
      } else {
        // Original subtle ember (homepage etc.)
        var g = ctx.createRadialGradient(this.ox, this.oy, 0, this.ox, this.oy, 10);
        g.addColorStop(0, 'rgba(255,200,90,0.14)');
        g.addColorStop(1, 'rgba(130,35,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath(); ctx.arc(this.ox, this.oy, 10, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = 'rgba(255,230,160,0.45)';
        ctx.beginPath(); ctx.arc(this.ox, this.oy, 1, 0, Math.PI * 2); ctx.fill();
      }
    }

    var cH = this.canvas.height;
    // Draw glyphs beneath, letters on top
    for (var j = 0; j < this.particles.length; j++) {
      this.particles[j].update(this.time, this.ox, this.oy, cH);
    }
    for (var k = 0; k < this.particles.length; k++) {
      if (!this.particles[k].isLetter) this.particles[k].draw(ctx);
    }
    for (var l = 0; l < this.particles.length; l++) {
      if (this.particles[l].isLetter) this.particles[l].draw(ctx);
    }

    this._raf = requestAnimationFrame(this._tick);
  };

  root.SmokeBrandChars = SmokeBrandChars;
}(window));
